import React, { useEffect, useState } from 'react';
import { 
    Box, 
    FormControl, 
    InputLabel, 
    MenuItem, 
    Select, 
    Button, 
    Typography,
    Alert,
    CircularProgress,
    SelectChangeEvent
} from '@mui/material';
import { fetchAuthSession } from 'aws-amplify/auth';

type Game = {
    gameid: string;
    firstname?: string;
    middlename?: string;
    lastname?: string;
    gamedate?: string;
    ready?: boolean;
};

type GameSelectorProps = {
    selectedDate?: string;
    onScheduleUpdate?: () => void;
};

const GameSelector: React.FC<GameSelectorProps> = ({ selectedDate, onScheduleUpdate }) => {
    const [games, setGames] = useState<Game[]>([]);
    const [selectedGameId, setSelectedGameId] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [scheduling, setScheduling] = useState(false);
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState<string>('');

    const loadGames = async () => {
        try {
            setLoading(true);
            const session = await fetchAuthSession();
            const requestOptions = {
                method: 'GET',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.tokens?.idToken}`
                }
            };
            
            const response = await fetch('https://api.admin.famousinitials.com/games', requestOptions);
            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.message || 'Failed to load games');
            }
            
            // Filter to only show games marked as ready
            const readyGames = data.filter((game: Game) => game.ready === true);
            
            // Sort games by firstname, then lastname for better UX
            readyGames.sort((a: Game, b: Game) => {
                const firstNameA = (a.firstname || '').toLowerCase();
                const firstNameB = (b.firstname || '').toLowerCase();
                const lastNameA = (a.lastname || '').toLowerCase();
                const lastNameB = (b.lastname || '').toLowerCase();
                
                // First compare by firstname
                if (firstNameA !== firstNameB) {
                    return firstNameA.localeCompare(firstNameB);
                }
                // If firstname is the same, compare by lastname
                return lastNameA.localeCompare(lastNameB);
            });
            
            setGames(readyGames);
        } catch (err) {
            console.error('Error loading games:', err);
            setError(err instanceof Error ? err.message : 'Failed to load games');
        } finally {
            setLoading(false);
        }
    };

    const scheduleGame = async () => {
        if (!selectedGameId || !selectedDate) {
            setError('Please select a game and date');
            return;
        }

        try {
            setScheduling(true);
            setError('');
            const session = await fetchAuthSession();
            
            const requestOptions = {
                method: 'PUT',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.tokens?.idToken}`
                },
                body: JSON.stringify({
                    gameDate: selectedDate,
                    gameId: selectedGameId
                })
            };
            
            const response = await fetch('https://api.admin.famousinitials.com/gameschedule', requestOptions);
            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.error || 'Failed to schedule game');
            }
            
            const selectedGame = games.find(g => g.gameid === selectedGameId);
            const gameName = selectedGame 
                ? `${selectedGame.firstname || ''} ${selectedGame.middlename || ''} ${selectedGame.lastname || ''}`.trim()
                : 'Selected game';
                
            setSuccess(`Successfully scheduled ${gameName} for ${selectedDate}`);
            setSelectedGameId('');
            
            // Notify parent component to refresh schedule
            if (onScheduleUpdate) {
                onScheduleUpdate();
            }
            
        } catch (err) {
            console.error('Error scheduling game:', err);
            setError(err instanceof Error ? err.message : 'Failed to schedule game');
        } finally {
            setScheduling(false);
        }
    };

    useEffect(() => {
        loadGames();
    }, []);

    // Clear messages when date changes
    useEffect(() => {
        setError('');
        setSuccess('');
    }, [selectedDate]);

    const handleGameChange = (event: SelectChangeEvent) => {
        setSelectedGameId(event.target.value);
        setError('');
        setSuccess('');
    };

    return (
        <Box sx={{ p: 3, minWidth: 300 }}>
            <Typography variant="h6" gutterBottom>
                Schedule Game
            </Typography>
            
            {selectedDate && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Selected Date: {selectedDate}
                </Typography>
            )}
            
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}
            
            {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    {success}
                </Alert>
            )}
            
            <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Select Game</InputLabel>
                <Select
                    value={selectedGameId}
                    label="Select Game"
                    onChange={handleGameChange}
                    disabled={loading || !selectedDate}
                >
                    {loading ? (
                        <MenuItem disabled>
                            <CircularProgress size={20} /> Loading games...
                        </MenuItem>
                    ) : (
                        games.map((game) => (
                            <MenuItem key={game.gameid} value={game.gameid}>
                                {`${game.firstname || ''} ${game.middlename || ''} ${game.lastname || ''}`.trim() || 'Unnamed Game'}
                            </MenuItem>
                        ))
                    )}
                </Select>
            </FormControl>
            
            <Button
                variant="contained"
                onClick={scheduleGame}
                disabled={!selectedGameId || !selectedDate || scheduling}
                fullWidth
            >
                {scheduling ? (
                    <>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Scheduling...
                    </>
                ) : (
                    'Schedule Game'
                )}
            </Button>
            
            {!selectedDate && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                    Select a date on the calendar to schedule a game
                </Typography>
            )}
        </Box>
    );
};

export default GameSelector;